import { format, isAfter, isBefore } from 'date-fns';

type FormatArgs = {
  granularity: string;
  level: string;
  data: any[];
  schoolIds: string[] | undefined;
  stakeholderTypeName: string | undefined;
  startDate: Date | null;
  endDate: Date | null;
};

export const formatActivityStatsData = ({
  granularity,
  level,
  data,
  schoolIds,
  stakeholderTypeName,
  startDate,
  endDate,
}: FormatArgs) => {
  let results: any[] = [];

  if (granularity === 'overall') {
    if (level === 'institution') {
      const activities: any = data;

      activities.forEach((activity: any) => {
        const { id, ...rest } = activity.node;
        const activityInterventions = activity.node.interventions;

        activityInterventions.forEach((intervention: any) => {
          const interventionInstitutions = intervention.institutions.filter((institution: any) =>
            schoolIds && schoolIds.length ? schoolIds.includes(institution.id) : institution
          );

          interventionInstitutions.forEach((institution: any) =>
            results.push({
              id: institution && institution.id,
              interventionInstitutionEmisNumber: institution && institution.emisNumber,
              interventionInstitutionName: institution && institution.name,
              interventionProgrammeId: intervention && intervention.programme && intervention.programme.id,
              interventionProgrammeName: intervention && intervention.programme && intervention.programme.name,
              activityId: id,
              ...rest,
            })
          );
        });
      });
    } else if (level === 'stakeholder') {
      const activities: any = data;

      activities.forEach(async (activity: any) => {
        const { id, ...rest } = activity.node;
        const activityInterventions = activity.node.interventions;

        activityInterventions.forEach((intervention: any) => {
          const interventionStakeholders = intervention.treatmentGroups.filter((treatmentGroup: any) => {
            const primarySchools =
              schoolIds && schoolIds.length && schoolIds.includes(treatmentGroup.primaryInstitution.id);
            const additionalSchools =
              schoolIds &&
              schoolIds.length &&
              treatmentGroup.institutions.some((institution: any) => schoolIds.includes(institution.id));
            const type = stakeholderTypeName && treatmentGroup.type === stakeholderTypeName;

            return ((primarySchools || additionalSchools) && type) || treatmentGroup;
          });

          interventionStakeholders.forEach((stakeholder: any) =>
            results.push({
              id: stakeholder && stakeholder.id,
              interventionProgrammeId: intervention && intervention.programme && intervention.programme.id,
              interventionProgrammeName: intervention && intervention.programme && intervention.programme.name,
              stakeholderEmisNumber:
                stakeholder && stakeholder.primaryInstitution && stakeholder.primaryInstitution.emisNumber,
              stakeholderInstitutionName:
                stakeholder && stakeholder.primaryInstitution && stakeholder.primaryInstitution.name,
              stakeholderType: stakeholder && stakeholder.type && stakeholder.type.name,
              stakeholderTrackingId: stakeholder && stakeholder.trackingId,
              stakeholderPersalNumber: stakeholder && stakeholder.persalNumber,
              stakeholderFirstName: stakeholder && stakeholder.firstName,
              stakeholderLastName: stakeholder && stakeholder.lastName,
              stakeholderNationalId: stakeholder && stakeholder.nationalID,
              stakeholderDOB:
                stakeholder && stakeholder.dateOfBirth && format(new Date(stakeholder.dateOfBirth), 'yyyy-MM-dd'),
              stakeholderContactNumber: stakeholder && stakeholder.contactNumber,
              stakeholderSessionsAttended: stakeholder && stakeholder.sessions && stakeholder.sessions.length,
              stakeholderCountryName: stakeholder && stakeholder.country && stakeholder.country.name,
              stakeholderProvinceName: stakeholder && stakeholder.province && stakeholder.province.name,
              stakeholderBranchName: stakeholder && stakeholder.branch,
              stakeholderCommunityType: stakeholder && stakeholder.communityType && stakeholder.communityType.name,
              stakeholderGender: stakeholder && stakeholder.gender && stakeholder.gender.name,
              activityId: id,
              ...rest,
            })
          );
        });
      });
    } else {
      const activities: any = data;

      activities.forEach((activity: any) => {
        const { id, ...rest } = activity.node;
        const activityInterventions = activity.node.interventions;

        activityInterventions.forEach((intervention: any) =>
          results.push({
            id: intervention && intervention.id,
            interventionProgrammeId: intervention && intervention.programme && intervention.programme.id,
            interventionProgrammeName: intervention && intervention.programme && intervention.programme.name,
            activityId: id,
            ...rest,
          })
        );
      });
    }
  }

  if (granularity === 'detailed') {
    if (level === 'institution') {
      if (!schoolIds || !schoolIds.length) {
        return results;
      }

      const activities: any = data;

      activities.forEach((activity: any) => {
        const { id, ...rest } = activity.node;
        const activitySessions = activity.node.sessions;

        activitySessions.forEach((session: any) => {
          if (session.actualDate) {
            const actualSessionDate = new Date(session.actualDate);
            if (startDate && isBefore(startDate, actualSessionDate)) {
              return;
            }

            if (endDate && isAfter(endDate, actualSessionDate)) {
              return;
            }
          }

          const sessionInstitutionStrings = new Set();
          session.sessionAttendees.forEach((attendee: any) =>
            sessionInstitutionStrings.add(JSON.stringify(attendee.primaryInstitution))
          );

          const sessionInstitutions: object[] = Array.from(sessionInstitutionStrings).map((institution: any) =>
            JSON.parse(institution)
          );
          const focusSchools = sessionInstitutions.filter((institution: any) => schoolIds.includes(institution.id));

          focusSchools.forEach((institution: any) =>
            results.push({
              id: `${institution.id}${session.id}`,

              interventionInstitutionEmisNumber: institution.emisNumber,
              interventionInstitutionName: institution.name,

              sessionName: session && session.name,
              sessionDescription: session && session.description,
              sessionPlannedDate: session && session.plannedDate && format(new Date(session.plannedDate), 'yyyy-MM-dd'),
              sessionActualDate: session && session.actualDate && format(new Date(session.actualDate), 'yyyy-MM-dd'),
              sessionPlannedDuration: session && session.plannedDuration,
              sessionActualDuration: session && session.actualDuration,
              sessionActualAttendees: session.sessionAttendees && session.sessionAttendees.length,
              sessionStatus: session && session.sessionStatus,
              sessionNotes: session && session.additionalNotes,
              sessionAttendanceRate: Number(
                ((session.sessionAttendees.length / rest.plannedAttendees) * 100).toFixed(2)
              ),

              activityId: activity.node.id,
              ...rest,
            })
          );
        });
      });
    } else if (level === 'stakeholder') {
      // get activity sessions
      const activities: any = data;

      activities.forEach((activity: any) => {
        const { id, ...rest } = activity.node;
        const activitySessions = activity.node.sessions;

        // populate results
        activitySessions.forEach((activitySession: any) => {
          if (activitySession.actualDate) {
            const actualSessionDate = new Date(activitySession.actualDate);
            if (startDate && isBefore(startDate, actualSessionDate)) {
              return;
            }

            if (endDate && isAfter(endDate, actualSessionDate)) {
              return;
            }
          }
          const actualAttendees: any[] = activitySession.sessionAttendees;

          actualAttendees.forEach((stakeholder: any) =>
            results.push({
              id: stakeholder && stakeholder.id,

              sessionName: activitySession && activitySession.name,
              sessionDescription: activitySession && activitySession.description,
              sessionPlannedDate:
                activitySession &&
                activitySession.plannedDate &&
                format(new Date(activitySession.plannedDate), 'yyyy-MM-dd'),
              sessionActualDate:
                activitySession &&
                activitySession.actualDate &&
                format(new Date(activitySession.actualDate), 'yyyy-MM-dd'),
              sessionPlannedDuration: activitySession && activitySession.plannedDuration,
              sessionActualDuration: activitySession && activitySession.actualDuration,
              sessionActualAttendees: actualAttendees.length,
              sessionStatus: activitySession && activitySession.sessionStatus,
              sessionNotes: activitySession && activitySession.additionalNotes,
              sessionAttendanceRate: Number(((actualAttendees.length / rest.plannedAttendees) * 100).toFixed(2)),

              stakeholderEmisNumber:
                stakeholder && stakeholder.primaryInstitution && stakeholder.primaryInstitution.emisNumber,
              stakeholderInstitutionName:
                stakeholder && stakeholder.primaryInstitution && stakeholder.primaryInstitution.name,
              stakeholderType: stakeholder && stakeholder.type && stakeholder.type.name,
              stakeholderTrackingId: stakeholder && stakeholder.trackingId,
              stakeholderPersalNumber: stakeholder && stakeholder.persalNumber,
              stakeholderFirstName: stakeholder && stakeholder.firstName,
              stakeholderLastName: stakeholder && stakeholder.lastName,
              stakeholderNationalId: stakeholder && stakeholder.nationalID,
              stakeholderDOB:
                stakeholder && stakeholder.dateOfBirth && format(new Date(stakeholder.dateOfBirth), 'yyyy-MM-dd'),
              stakeholderContactNumber: stakeholder && stakeholder.contactNumber,
              stakeholderSessionsAttended: stakeholder && stakeholder.sessions && stakeholder.sessions.length,
              stakeholderCountryName: stakeholder && stakeholder.country && stakeholder.country.name,
              stakeholderProvinceName: stakeholder && stakeholder.province && stakeholder.province.name,
              stakeholderBranchName: stakeholder && stakeholder.branch,
              stakeholderCommunityType: stakeholder && stakeholder.communityType && stakeholder.communityType.name,
              stakeholderGender: stakeholder && stakeholder.gender && stakeholder.gender.name,
              activityId: id,
              ...rest,
            })
          );
        });
      });
      // --- END OF IF ---
    } else {
      const activities: any = data;

      activities.forEach((activity: any) => {
        const { id, ...rest } = activity.node;
        const activitySessions = activity.node.sessions;

        activitySessions.forEach((session: any) => {
          if (session.actualDate) {
            const actualSessionDate = new Date(session.actualDate);
            if (startDate && isBefore(startDate, actualSessionDate)) {
              return;
            }

            if (endDate && isAfter(endDate, actualSessionDate)) {
              return;
            }
          }
          const actualAttendees: any[] = session.sessionAttendees;
          const institutions: any = new Set();

          actualAttendees.forEach(
            attendee => attendee.primaryInstitution && institutions.add(attendee.primaryInstitution.name)
          );

          results.push({
            id: session.id,

            sessionName: session.name,
            sessionDescription: session.description,
            sessionInstitutions: Array.from(institutions)[0],
            sessionPlannedDate: session.plannedDate && format(new Date(session.plannedDate), 'yyyy-MM-dd'),
            sessionActualDate: session.actualDate && format(new Date(session.actualDate), 'yyyy-MM-dd'),
            sessionPlannedDuration: session.plannedDuration,
            sessionActualDuration: session.actualDuration,
            sessionActualAttendees: session.sessionAttendees && session.sessionAttendees.length,
            sessionStatus: session.sessionStatus,
            sessionNotes: session.additionalNotes,
            sessionAttendanceRate: Number(((session.sessionAttendees.length / rest.plannedAttendees) * 100).toFixed(2)),

            activityId: activity.node.id,
            ...rest,
          });
        });
      });
    }
  }

  return results;
};
